import { useState, useMemo } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import Button from 'domains/core/components/Button';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import { Campaign, ABTestType } from 'domains/campaigns/types';
import { SendOutlined } from '@mui/icons-material';
import QueryKeys from 'hooks/queries/keys';
import useScheduleABTestWinner from 'hooks/mutations/useScheduleABTestWinner';
import WinnerPreviewStep from './WinnerPreviewStep';
import WinnerScheduleStep from './WinnerScheduleStep';

type WinnerScheduleModalProps = {
    campaign: Campaign;
    isOpen: boolean;
    handleModalClose: () => void;
};

enum ModalStep {
    PREVIEW = 0,
    SCHEDULE = 1,
}

/**
 * Calculates the next occurrence of the same day of week and time
 * Example: If the original date was Monday 10:00 AM, this returns the next Monday at 10:00 AM
 */
const getNextOccurrenceOfSameDayAndTime = (originalTimestamp: string): string | null => {
    const originalDateTime = DateTime.fromISO(originalTimestamp);
    if (!originalDateTime.isValid) {
        return null;
    }

    const now = DateTime.now();
    const originalDayOfWeek = originalDateTime.weekday; // 1 = Monday, 7 = Sunday
    const originalHour = originalDateTime.hour;
    const originalMinute = originalDateTime.minute;

    // Calculate how many days until the next occurrence of the same weekday
    let daysUntilNext = originalDayOfWeek - now.weekday;

    // If it's the same day but the time has passed, or if the day has already passed this week, add 7 days
    if (
        daysUntilNext < 0 ||
        (daysUntilNext === 0 && now.hour > originalHour) ||
        (daysUntilNext === 0 && now.hour === originalHour && now.minute >= originalMinute)
    ) {
        daysUntilNext += 7;
    }

    const nextOccurrence = now.plus({ days: daysUntilNext }).set({
        hour: originalHour,
        minute: originalMinute,
        second: 0,
        millisecond: 0,
    });

    return nextOccurrence.toISO();
};

const WinnerScheduleModal = ({ campaign, isOpen, handleModalClose }: WinnerScheduleModalProps) => {
    const queryClient = useQueryClient();
    const [currentStep, setCurrentStep] = useState<ModalStep>(ModalStep.PREVIEW);
    const [scheduledTimestamp, setScheduledTimestamp] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Calculate initial scheduled timestamp for send time AB tests
    const initialScheduledTimestamp = useMemo(() => {
        const isSendTimeTest = campaign.abTestConfig?.testOn === ABTestType.SEND_TIME;
        if (!isSendTimeTest) {
            return null;
        }

        // Get the winner send's original scheduled time
        const winnerSendId = campaign.abTestConfig?.winnerSendId;
        const winnerSend = campaign.sends.find((send) => send.id === winnerSendId);

        if (!winnerSend?.scheduledTimestamp) {
            return null;
        }

        // Calculate next occurrence of the same day and time
        return getNextOccurrenceOfSameDayAndTime(winnerSend.scheduledTimestamp);
    }, [campaign.abTestConfig?.testOn, campaign.abTestConfig?.winnerSendId, campaign.sends]);

    // API mutation hook for scheduling the winner
    const { mutate: scheduleWinner, isLoading: isScheduling, isError, error } = useScheduleABTestWinner({
        onSuccess: () => {
            // Successfully scheduled winner - invalidate campaign query to refetch updated data
            queryClient.invalidateQueries(QueryKeys.CURATION_ID(campaign.id));
            // Reset modal state
            setCurrentStep(ModalStep.PREVIEW);
            setScheduledTimestamp(null);
            handleModalClose();
        },
    });

    const handleNext = () => {
        if (currentStep === ModalStep.PREVIEW) {
            setCurrentStep(ModalStep.SCHEDULE);
        }
    };

    const handleBack = () => {
        if (currentStep === ModalStep.SCHEDULE) {
            setCurrentStep(ModalStep.PREVIEW);
        }
    };

    const handleScheduleChange = (timestamp: string | null) => {
        setScheduledTimestamp(timestamp);
    };

    const handleScheduleSend = () => {
        if (!scheduledTimestamp) {
            setValidationError('Please select a valid date and time before scheduling');
            return;
        }

        // Clear any previous validation errors
        setValidationError(null);

        // Call the API to schedule the winner
        scheduleWinner({
            campaignId: campaign.id,
            scheduledTimestamp: scheduledTimestamp,
        });
    };

    const handleClose = () => {
        setCurrentStep(ModalStep.PREVIEW); // Reset to first step
        setScheduledTimestamp(null);
        setValidationError(null); // Clear validation error
        handleModalClose();
    };

    const getStepTitle = () => {
        switch (currentStep) {
            case ModalStep.PREVIEW:
                return 'send winner';
            case ModalStep.SCHEDULE:
                return 'send winner';
            default:
                return 'send winner';
        }
    };

    const isNextDisabled = false; // Preview step doesn't have validation
    const isSendDisabled = !scheduledTimestamp; // Schedule step requires a valid timestamp

    return (
        <>
            {isError && !isScheduling && <ErrorSnackbar errorMessage={error?.message || 'Failed to schedule winner'} />}
            {validationError && <ErrorSnackbar errorMessage={validationError} />}
            <Dialog maxWidth="md" fullWidth open={isOpen} onClose={handleClose}>
                <DialogTitle>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <SendOutlined />
                        <Typography variant="h5" sx={{ fontWeight: 500 }}>
                            {getStepTitle()}
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ minHeight: '400px' }}>
                    {currentStep === ModalStep.PREVIEW && <WinnerPreviewStep campaign={campaign} />}
                    {currentStep === ModalStep.SCHEDULE && (
                        <WinnerScheduleStep
                            campaign={campaign}
                            onScheduleChange={handleScheduleChange}
                            initialScheduledTimestamp={initialScheduledTimestamp}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: 3, justifyContent: 'space-between' }}>
                    <Button variant="text" onClick={handleClose} disabled={isScheduling}>
                        Cancel
                    </Button>
                    <Stack direction="row" spacing={2}>
                        {currentStep === ModalStep.SCHEDULE && (
                            <Button variant="outlined" onClick={handleBack} disabled={isScheduling}>
                                Back
                            </Button>
                        )}
                        {currentStep === ModalStep.PREVIEW && (
                            <Button variant="contained" onClick={handleNext} disabled={isNextDisabled}>
                                Next
                            </Button>
                        )}
                        {currentStep === ModalStep.SCHEDULE && (
                            <Button
                                variant="contained"
                                onClick={handleScheduleSend}
                                disabled={isSendDisabled || isScheduling}
                            >
                                {isScheduling ? 'Scheduling...' : 'Send winner'}
                            </Button>
                        )}
                    </Stack>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WinnerScheduleModal;
